import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Check, Plus, Trash2, User, Users, Heart, AlertCircle, AlertTriangle, Edit } from 'lucide-react';
import { PublicLayout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { createRSVP, getRSVP, updateRSVPPublic } from '@/lib/api';

const STEPS = [
  { id: 1, title: 'Vos coordonnées', icon: User },
  { id: 2, title: 'Votre présence', icon: Heart },
  { id: 3, title: 'Les participants', icon: Users },
  { id: 4, title: 'Informations', icon: AlertCircle },
  { id: 5, title: 'Confirmation', icon: Check },
];

const RSVPPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('edit');
  const [isEditMode, setIsEditMode] = useState(false);
  const [loadingRSVP, setLoadingRSVP] = useState(false);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showConfirmParticipants, setShowConfirmParticipants] = useState(false);
  const [formData, setFormData] = useState({
    family_name: '',
    first_name: '',
    email: '',
    phone: '',
    attending: null,
    absence_message: '',
    adults: [{ civility: 'Monsieur', first_name: '', last_name: '' }],
    children: [],
    dietary_restrictions: '',
    message_to_couple: '',
    consent: false,
    brunch_attending: null,
    brunch_count: 0,
  });

  // Load existing RSVP if editing
  useEffect(() => {
    if (editId) {
      loadExistingRSVP(editId);
    }
  }, [editId]);

  const loadExistingRSVP = async (id) => {
    setLoadingRSVP(true);
    try {
      const response = await getRSVP(id);
      const rsvp = response.data;
      setFormData({
        family_name: rsvp.family_name || '',
        first_name: rsvp.adults?.[0]?.first_name || '',
        email: rsvp.email || '',
        phone: rsvp.phone || '',
        attending: rsvp.attending,
        absence_message: rsvp.absence_message || '',
        adults: rsvp.adults?.length > 0 ? rsvp.adults.map(a => ({
          civility: a.civility || 'Monsieur',
          first_name: a.first_name || '',
          last_name: a.last_name || ''
        })) : [{ civility: 'Monsieur', first_name: '', last_name: '' }],
        children: rsvp.children || [],
        dietary_restrictions: rsvp.dietary_restrictions || '',
        message_to_couple: rsvp.message_to_couple || '',
        consent: true,
        brunch_attending: rsvp.brunch_attending ?? null,
        brunch_count: rsvp.brunch_count || 0,
      });
      setIsEditMode(true);
      toast.info('Modifiez vos informations puis validez');
    } catch (error) {
      toast.error('Impossible de charger votre réponse');
      navigate('/espace-invite');
    } finally {
      setLoadingRSVP(false);
    }
  };

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const addAdult = () => {
    setFormData((prev) => ({
      ...prev,
      adults: [...prev.adults, { civility: 'Monsieur', first_name: '', last_name: '' }],
    }));
  };

  const removeAdult = (index) => {
    if (formData.adults.length > 1) {
      setFormData((prev) => ({
        ...prev,
        adults: prev.adults.filter((_, i) => i !== index),
      }));
    }
  };

  const updateAdult = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      adults: prev.adults.map((adult, i) => (i === index ? { ...adult, [field]: value } : adult)),
    }));
  };

  const addChild = () => {
    setFormData((prev) => ({
      ...prev,
      children: [...prev.children, { first_name: '', age: '' }],
    }));
  };

  const removeChild = (index) => {
    setFormData((prev) => ({
      ...prev,
      children: prev.children.filter((_, i) => i !== index),
    }));
  };

  const updateChild = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      children: prev.children.map((child, i) => (i === index ? { ...child, [field]: value } : child)),
    }));
  };

  const canProceed = () => {
    switch (step) {
      case 1:
        return formData.family_name && formData.first_name && formData.email && formData.phone;
      case 2:
        return formData.attending !== null;
      case 3:
        if (!formData.attending) return true;
        return formData.adults.every((a) => a.first_name && a.last_name) && formData.children.every((c) => c.first_name && c.age);
      case 4:
        return true;
      case 5:
        return formData.consent;
      default:
        return true;
    }
  };

  const handleSubmit = async () => {
    if (!formData.consent) {
      toast.error('Veuillez accepter la politique de confidentialité');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        children: formData.children.map((c) => ({ ...c, age: parseInt(c.age) || 0 })),
      };
      
      let response;
      if (isEditMode && editId) {
        // Update existing RSVP
        response = await updateRSVPPublic(editId, payload);
        toast.success('Vos modifications ont été enregistrées !');
        navigate('/espace-invite');
      } else {
        // Create new RSVP
        response = await createRSVP(payload);
        toast.success('Votre réponse a été enregistrée !');
        
        if (formData.attending) {
          navigate(`/hebergement/${response.data.id}`);
        } else {
          navigate(`/confirmation/${response.data.id}`);
        }
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Une erreur est survenue';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const nextStep = () => {
    if (canProceed()) {
      if (step === 2 && !formData.attending) {
        setStep(5); // Skip to confirmation if not attending
      } else if (step === 2 && formData.attending) {
        // Pre-fill first adult with the person who registered
        setFormData((prev) => ({
          ...prev,
          adults: [
            { 
              civility: prev.adults[0]?.civility || 'Monsieur', 
              first_name: prev.first_name, 
              last_name: prev.family_name 
            },
            ...prev.adults.slice(1)
          ],
        }));
        setStep(3);
      } else if (step === 3) {
        // Show confirmation dialog before going to step 4
        setShowConfirmParticipants(true);
      } else {
        setStep((prev) => Math.min(prev + 1, 5));
      }
    }
  };

  const confirmParticipantsAndProceed = () => {
    setShowConfirmParticipants(false);
    setStep(4);
  };

  const totalParticipants = formData.adults.length + formData.children.length;

  const prevStep = () => {
    if (step === 5 && !formData.attending) {
      setStep(2); // Go back to attendance if skipped
    } else {
      setStep((prev) => Math.max(prev - 1, 1));
    }
  };

  return (
    <PublicLayout>
      {/* Hero */}
      <section className="py-16 md:py-24 bg-terracotta-light/20">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}>
            <h1 className="font-display text-4xl md:text-5xl text-charcoal mb-4" data-testid="rsvp-title">
              Confirmez votre présence
            </h1>
            <p className="text-stone">
              Confirmez votre présence avant le 1er mai 2026
            </p>
          </motion.div>
        </div>
      </section>

      {/* Progress */}
      <section className="py-8 bg-white border-b border-stone-light">
        <div className="max-w-2xl mx-auto px-6">
          <div className="flex items-center justify-between">
            {STEPS.map((s, i) => (
              <div key={s.id} className="flex items-center">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                    step >= s.id ? 'bg-terracotta text-white' : 'bg-stone-light text-stone'
                  }`}
                >
                  {step > s.id ? <Check className="w-5 h-5" /> : <s.icon className="w-5 h-5" />}
                </div>
                {i < STEPS.length - 1 && (
                  <div className={`hidden sm:block w-12 lg:w-20 h-0.5 mx-2 ${step > s.id ? 'bg-terracotta' : 'bg-stone-light'}`} />
                )}
              </div>
            ))}
          </div>
          <p className="text-center mt-4 text-sm text-stone font-medium">
            Étape {step} : {STEPS[step - 1].title}
          </p>
        </div>
      </section>

      {/* Form */}
      <section className="section-padding">
        <div className="max-w-xl mx-auto">
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="card-elegant"
            >
              {/* Step 1: Coordinates */}
              {step === 1 && (
                <div className="space-y-6">
                  <div>
                    <Label htmlFor="family_name" className="text-charcoal font-medium">
                      Nom *
                    </Label>
                    <Input
                      id="family_name"
                      data-testid="input-family-name"
                      value={formData.family_name}
                      onChange={(e) => updateField('family_name', e.target.value)}
                      placeholder="Votre nom"
                      className="input-elegant mt-2"
                    />
                  </div>
                  <div>
                    <Label htmlFor="first_name" className="text-charcoal font-medium">
                      Prénom *
                    </Label>
                    <Input
                      id="first_name"
                      data-testid="input-first-name"
                      value={formData.first_name}
                      onChange={(e) => updateField('first_name', e.target.value)}
                      placeholder="Votre prénom"
                      className="input-elegant mt-2"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-charcoal font-medium">
                      Adresse email *
                    </Label>
                    <Input
                      id="email"
                      data-testid="input-email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => updateField('email', e.target.value)}
                      placeholder="votre@email.com"
                      className="input-elegant mt-2"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-charcoal font-medium">
                      Numéro de téléphone *
                    </Label>
                    <Input
                      id="phone"
                      data-testid="input-phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => updateField('phone', e.target.value)}
                      placeholder="06 12 34 56 78"
                      className="input-elegant mt-2"
                    />
                  </div>
                </div>
              )}

              {/* Step 2: Attendance */}
              {step === 2 && (
                <div className="space-y-6">
                  <p className="text-center text-charcoal font-display text-xl mb-8">
                    Serez-vous présent(s) le 5 septembre 2026 ?
                  </p>
                  <RadioGroup
                    value={formData.attending?.toString()}
                    onValueChange={(value) => updateField('attending', value === 'true')}
                    className="space-y-4"
                  >
                    <div
                      data-testid="radio-attending-yes"
                      className={`flex items-center space-x-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        formData.attending === true ? 'border-green-500 bg-green-50' : 'border-stone-light hover:border-green-300'
                      }`}
                      onClick={() => updateField('attending', true)}
                    >
                      <RadioGroupItem value="true" id="attending-yes" className="text-green-500" />
                      <Label htmlFor="attending-yes" className="cursor-pointer flex-1 flex items-center gap-3">
                        <span className="text-2xl">😊</span>
                        <span className="font-medium text-charcoal">Je serai présent(e) / Nous serons présents</span>
                      </Label>
                    </div>
                    <div
                      data-testid="radio-attending-no"
                      className={`flex items-center space-x-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        formData.attending === false ? 'border-orange-500 bg-orange-50' : 'border-stone-light hover:border-orange-300'
                      }`}
                      onClick={() => updateField('attending', false)}
                    >
                      <RadioGroupItem value="false" id="attending-no" className="text-orange-500" />
                      <Label htmlFor="attending-no" className="cursor-pointer flex-1 flex items-center gap-3">
                        <span className="text-2xl">😢</span>
                        <span className="font-medium text-charcoal">Je ne pourrai pas être présent(e) / Nous ne pourrons pas être présents</span>
                      </Label>
                    </div>
                  </RadioGroup>

                  {formData.attending === false && (
                    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="mt-6">
                      <Label htmlFor="absence_message" className="text-charcoal font-medium">
                        Un message pour les mariés ?
                      </Label>
                      <Textarea
                        id="absence_message"
                        data-testid="input-absence-message"
                        value={formData.absence_message}
                        onChange={(e) => updateField('absence_message', e.target.value)}
                        placeholder="Votre message..."
                        className="input-elegant mt-2 min-h-[100px]"
                      />
                    </motion.div>
                  )}
                </div>
              )}

              {/* Step 3: Participants */}
              {step === 3 && (
                <div className="space-y-6">
                  {/* Warning message */}
                  <div className="p-4 bg-terracotta/10 border border-terracotta/30 rounded-xl">
                    <p className="text-sm text-charcoal flex items-start gap-2">
                      <AlertCircle className="w-5 h-5 text-terracotta flex-shrink-0 mt-0.5" />
                      <span>
                        <strong>Important :</strong> Merci d'inscrire toutes les personnes qui seront présentes à notre mariage (vous y compris). 
                        Chaque invité doit être enregistré pour l'organisation du repas et des places.
                      </span>
                    </p>
                  </div>

                  {/* Adults */}
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-display text-xl text-charcoal">Adultes</h3>
                      <Button
                        data-testid="btn-add-adult"
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={addAdult}
                        className="text-terracotta border-terracotta hover:bg-terracotta hover:text-white"
                      >
                        <Plus className="w-4 h-4 mr-1" /> Ajouter
                      </Button>
                    </div>
                    <div className="space-y-4">
                      {formData.adults.map((adult, index) => (
                        <div key={index} className="p-4 bg-bone/50 rounded-xl space-y-3">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-charcoal">
                              {index === 0 ? 'Vous' : `Invité ${index + 1}`}
                            </span>
                            {formData.adults.length > 1 && (
                              <Button
                                data-testid={`btn-remove-adult-${index}`}
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => removeAdult(index)}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            <div>
                              <Label className="text-xs text-stone">Civilité *</Label>
                              <Select
                                value={adult.civility}
                                onValueChange={(value) => updateAdult(index, 'civility', value)}
                              >
                                <SelectTrigger data-testid={`select-civility-${index}`} className="mt-1">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="Monsieur">Monsieur</SelectItem>
                                  <SelectItem value="Madame">Madame</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label className="text-xs text-stone">Prénom *</Label>
                              <Input
                                data-testid={`input-adult-firstname-${index}`}
                                value={adult.first_name}
                                onChange={(e) => updateAdult(index, 'first_name', e.target.value)}
                                placeholder="Prénom"
                                className="input-elegant mt-1"
                              />
                            </div>
                            <div>
                              <Label className="text-xs text-stone">Nom *</Label>
                              <Input
                                data-testid={`input-adult-lastname-${index}`}
                                value={adult.last_name}
                                onChange={(e) => updateAdult(index, 'last_name', e.target.value)}
                                placeholder="Nom"
                                className="input-elegant mt-1"
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Children */}
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-display text-xl text-charcoal">Enfants</h3>
                      <Button
                        data-testid="btn-add-child"
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={addChild}
                        className="text-terracotta border-terracotta hover:bg-terracotta hover:text-white"
                      >
                        <Plus className="w-4 h-4 mr-1" /> Ajouter
                      </Button>
                    </div>
                    {formData.children.length === 0 ? (
                      <p className="text-stone text-sm text-center py-4 bg-stone-light/30 rounded-lg">
                        Pas d'enfant pour le moment
                      </p>
                    ) : (
                      <div className="space-y-4">
                        {formData.children.map((child, index) => (
                          <div key={index} className="flex gap-3 items-end">
                            <div className="flex-1">
                              <Label className="text-xs text-stone">Prénom *</Label>
                              <Input
                                data-testid={`input-child-name-${index}`}
                                value={child.first_name}
                                onChange={(e) => updateChild(index, 'first_name', e.target.value)}
                                placeholder="Prénom"
                                className="input-elegant mt-1"
                              />
                            </div>
                            <div className="w-20">
                              <Label className="text-xs text-stone">Âge *</Label>
                              <Input
                                data-testid={`input-child-age-${index}`}
                                type="number"
                                min="0"
                                max="17"
                                value={child.age}
                                onChange={(e) => updateChild(index, 'age', e.target.value)}
                                placeholder="Âge"
                                className="input-elegant mt-1"
                              />
                            </div>
                            <Button
                              data-testid={`btn-remove-child-${index}`}
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => removeChild(index)}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Step 4: Additional Info */}
              {step === 4 && (
                <div className="space-y-6">
                  {/* Section Brunch */}
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
                    <div className="flex items-center gap-2 mb-4">
                      <span className="text-2xl">🥐</span>
                      <h3 className="font-display text-lg text-charcoal">Brunch du Dimanche 6 septembre</h3>
                    </div>
                    <p className="text-stone text-sm mb-4">
                      Prolongez la fête avec nous ! Un brunch convivial est organisé le lendemain au domaine.
                    </p>
                    
                    <div className="space-y-4">
                      <div>
                        <Label className="text-charcoal font-medium mb-3 block">
                          Serez-vous présent(s) au brunch ?
                        </Label>
                        <RadioGroup
                          value={formData.brunch_attending === true ? 'yes' : formData.brunch_attending === false ? 'no' : ''}
                          onValueChange={(value) => {
                            const attending = value === 'yes';
                            updateField('brunch_attending', attending);
                            if (attending) {
                              // Set default count to total participants
                              const totalPeople = formData.adults.length + formData.children.length;
                              updateField('brunch_count', totalPeople);
                            } else {
                              updateField('brunch_count', 0);
                            }
                          }}
                          className="flex gap-4"
                        >
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="yes" id="brunch-yes" data-testid="brunch-yes" />
                            <Label htmlFor="brunch-yes" className="cursor-pointer">Oui, avec plaisir !</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="no" id="brunch-no" data-testid="brunch-no" />
                            <Label htmlFor="brunch-no" className="cursor-pointer">Non, malheureusement</Label>
                          </div>
                        </RadioGroup>
                      </div>
                      
                      {formData.brunch_attending === true && (
                        <div>
                          <Label className="text-charcoal font-medium mb-2 block">
                            Nombre de personnes présentes au brunch
                          </Label>
                          <Select
                            value={String(formData.brunch_count)}
                            onValueChange={(value) => updateField('brunch_count', parseInt(value))}
                          >
                            <SelectTrigger className="w-32" data-testid="brunch-count-select">
                              <SelectValue placeholder="Nombre" />
                            </SelectTrigger>
                            <SelectContent>
                              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                                <SelectItem key={n} value={String(n)}>{n} personne{n > 1 ? 's' : ''}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="dietary" className="text-charcoal font-medium">
                      Allergies ou régimes alimentaires
                    </Label>
                    <Textarea
                      id="dietary"
                      data-testid="input-dietary"
                      value={formData.dietary_restrictions}
                      onChange={(e) => updateField('dietary_restrictions', e.target.value)}
                      placeholder="Végétarien, sans gluten, allergies..."
                      className="input-elegant mt-2 min-h-[80px]"
                    />
                  </div>
                  <div>
                    <Label htmlFor="message" className="text-charcoal font-medium">
                      Un message pour Mélanie & Cédric
                    </Label>
                    <Textarea
                      id="message"
                      data-testid="input-message"
                      value={formData.message_to_couple}
                      onChange={(e) => updateField('message_to_couple', e.target.value)}
                      placeholder="Votre message personnel..."
                      className="input-elegant mt-2 min-h-[100px]"
                    />
                  </div>
                </div>
              )}

              {/* Step 5: Confirmation */}
              {step === 5 && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <Heart className="w-12 h-12 text-terracotta mx-auto mb-4" fill="#D4A373" />
                    <h3 className="font-display text-2xl text-charcoal mb-2">Récapitulatif</h3>
                  </div>

                  <div className="bg-bone rounded-xl p-4 space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-stone">Famille</span>
                      <span className="font-medium text-charcoal">{formData.family_name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-stone">Email</span>
                      <span className="font-medium text-charcoal">{formData.email}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-stone">Présence</span>
                      <span className={`font-medium ${formData.attending ? 'text-sage' : 'text-red-500'}`}>
                        {formData.attending ? 'Présent(s)' : 'Absent(s)'}
                      </span>
                    </div>
                    {formData.attending && (
                      <>
                        <div className="flex justify-between">
                          <span className="text-stone">Adultes</span>
                          <span className="font-medium text-charcoal">{formData.adults.length}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-stone">Enfants</span>
                          <span className="font-medium text-charcoal">{formData.children.length}</span>
                        </div>
                        <div className="flex justify-between border-t border-stone-light pt-3 mt-3">
                          <span className="text-stone">🥐 Brunch dimanche</span>
                          <span className={`font-medium ${formData.brunch_attending ? 'text-sage' : formData.brunch_attending === false ? 'text-red-500' : 'text-stone'}`}>
                            {formData.brunch_attending === true 
                              ? `Oui (${formData.brunch_count} pers.)` 
                              : formData.brunch_attending === false 
                                ? 'Non' 
                                : 'Non renseigné'}
                          </span>
                        </div>
                      </>
                    )}
                  </div>

                  <div className="flex items-start space-x-3 p-4 bg-terracotta/5 rounded-xl border border-terracotta/20">
                    <Checkbox
                      id="consent"
                      data-testid="checkbox-consent"
                      checked={formData.consent}
                      onCheckedChange={(checked) => updateField('consent', checked)}
                      className="mt-1"
                    />
                    <Label htmlFor="consent" className="text-sm text-charcoal cursor-pointer">
                      J'accepte que mes données soient utilisées dans le cadre de l'organisation du mariage.{' '}
                      <a href="/confidentialite" className="text-terracotta underline" target="_blank">
                        Politique de confidentialité
                      </a>
                    </Label>
                  </div>
                </div>
              )}
            </motion.div>
          </AnimatePresence>

          {/* Navigation */}
          <div className="flex justify-between mt-8">
            <Button
              data-testid="btn-prev-step"
              type="button"
              variant="outline"
              onClick={prevStep}
              disabled={step === 1}
              className="btn-secondary"
            >
              <ChevronLeft className="w-4 h-4 mr-2" />
              Précédent
            </Button>

            {step < 5 ? (
              <Button
                data-testid="btn-next-step"
                type="button"
                onClick={nextStep}
                disabled={!canProceed()}
                className="btn-primary"
              >
                Suivant
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                data-testid="btn-submit-rsvp"
                type="button"
                onClick={handleSubmit}
                disabled={!canProceed() || loading}
                className="btn-primary"
              >
                {loading ? 'Envoi...' : 'Valider ma réponse'}
                <Check className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        </div>
      </section>

      {/* Confirmation Dialog for Participants */}
      <Dialog open={showConfirmParticipants} onOpenChange={setShowConfirmParticipants}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-charcoal">
              <AlertTriangle className="w-5 h-5 text-terracotta" />
              Confirmation du nombre de participants
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-charcoal mb-4">
              Vous avez indiqué venir à <strong className="text-terracotta text-lg">{totalParticipants} personne{totalParticipants > 1 ? 's' : ''}</strong> à notre mariage :
            </p>
            <div className="bg-bone rounded-lg p-4 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-stone">Adultes</span>
                <span className="font-medium text-charcoal">{formData.adults.length}</span>
              </div>
              {formData.adults.map((adult, i) => (
                <div key={i} className="flex justify-between pl-4 text-xs">
                  <span className="text-stone">{adult.civility}</span>
                  <span className="text-charcoal">{adult.first_name} {adult.last_name}</span>
                </div>
              ))}
              {formData.children.length > 0 && (
                <>
                  <div className="flex justify-between pt-2 border-t border-stone-light">
                    <span className="text-stone">Enfants</span>
                    <span className="font-medium text-charcoal">{formData.children.length}</span>
                  </div>
                  {formData.children.map((child, i) => (
                    <div key={i} className="flex justify-between pl-4 text-xs">
                      <span className="text-charcoal">{child.first_name}</span>
                      <span className="text-stone">{child.age} ans</span>
                    </div>
                  ))}
                </>
              )}
            </div>
            <p className="text-sm text-stone mt-4">
              Confirmez-vous ces informations ?
            </p>
          </div>
          <DialogFooter className="flex flex-col-reverse sm:flex-row gap-2">
            <Button 
              variant="outline" 
              onClick={() => setShowConfirmParticipants(false)}
              className="w-full sm:flex-1"
            >
              Modifier
            </Button>
            <Button 
              data-testid="btn-confirm-participants"
              onClick={confirmParticipantsAndProceed}
              className="btn-primary w-full sm:flex-1"
            >
              Confirmer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PublicLayout>
  );
};

export default RSVPPage;
